import { Check } from '@untitled-ui/icons-react';
import React, { useMemo } from 'react';

import { Button, Card, FeaturedIcon } from '@myhealth-raccoon/components';

import { ScrollablePanelBody, ScrollablePanelFooter, ScrollablePanelHeader } from './scrollablePanel';
import { ScrollablePanel } from './scrollablePanel/ScrollablePanel';
import { AppointmentType } from 'src/services/availability';
import { ExpressTelehealthPurpose, expressTelehealthPurpose } from 'src/utils/appointmentTypes';

type Props = {
    isOpen: boolean;
    onClose(): void;
    appointmentType: AppointmentType;
    onAgree(reason: ExpressTelehealthPurpose): void;
};

function ClinicalDiscretion(reason: ExpressTelehealthPurpose): React.ReactNode {
    switch (reason) {
        case ExpressTelehealthPurpose.MedicalCertificate:
            return (
                <p>
                    <strong>Respect Clinical Discretion:</strong> The GP reserves the right to decline a certificate or
                    mandate an in-person assessment if your clinical presentation warrants it.
                </p>
            );
        case ExpressTelehealthPurpose.Script:
            return (
                <p>
                    <strong>GP Clinical Discretion:</strong> The GP reserves the right to decline a renewal, or request
                    an in-person assessment if clinically indicated.
                </p>
            );
        case ExpressTelehealthPurpose.Referral:
            return (
                <p>
                    <strong>GP Clinical Discretion:</strong> The GP reserves the right to decline a renewal or update,
                    or request an in-person assessment if clinically indicated.
                </p>
            );
        case ExpressTelehealthPurpose.Diagnostics:
            return (
                <p>
                    <strong>Respect Clinical Discretion:</strong> The GP reserves the right to decline a request or
                    mandate an in-person assessment for clinical safety or if your presentation warrants it.
                </p>
            );
        case ExpressTelehealthPurpose.Unknown:
            return null;
    }
}
type Eligibility = {
    criteria: React.ReactNode[];
    notSuitable: React.ReactNode[];
};

function getEligibility(reason: ExpressTelehealthPurpose): Eligibility {
    switch (reason) {
        case ExpressTelehealthPurpose.MedicalCertificate:
            return {
                criteria: [
                    <>
                        <h4>Minor Illness Only</h4>
                        <p>
                            Minor health concerns only - Available for simple, non-urgent conditions that don't require
                            a physical examination.
                        </p>
                    </>,
                    <>
                        <h4>Recent Attendance</h4>
                        <p>
                            Your child must have already seen a Myhealth doctor for their current illness before
                            requesting a clearance certificate.
                        </p>
                    </>,
                ],
                notSuitable: [
                    <>
                        <h4>Exclusions Apply </h4>
                        <p>Excluded from this service are:</p>
                        <ul>
                            <li>Workers' Compensation certificate</li>
                            <li>Return-to-work clearances</li>
                            <li>Centrelink Certificates</li>
                            <li>Certificates or letters for court or for legal purpose</li>
                            <li>Any other forms</li>
                        </ul>
                    </>,
                    <>
                        <h4>Backdating</h4>
                        <p>Certificates cannot be backdated and may only start from the date of your consultation.</p>
                    </>,
                ],
            };
        case ExpressTelehealthPurpose.Script:
            return {
                criteria: [
                    <>
                        <h4>Existing prescriptions only</h4>
                        <p>
                            Available for medications you've previously been prescribed and continue to take regularly.
                        </p>
                    </>,
                    <>
                        <h4>Recent attendance</h4>
                        <p>You must have had a face-to-face appointment with a Myhealth doctor in the last 6 months.</p>
                    </>,
                ],
                notSuitable: [
                    <>
                        <h4>New medications or health concerns</h4>
                        <p>
                            This service can't be used to start new medications or discuss new symptoms or health
                            concerns.
                        </p>
                    </>,
                    <>
                        <h4>Restricted medications</h4>
                        <p>
                            We can't provide prescriptions for controlled or dependency-forming medications, including
                            medicines such as OxyContin, Tramadol and Valium.
                        </p>
                    </>,
                ],
            };
        case ExpressTelehealthPurpose.Referral:
            return {
                criteria: [
                    <>
                        <h4>Existing referrals only</h4>
                        <p>
                            We can renew referrals to specialists or allied health providers that were originally issued
                            by a Myhealth doctor.
                        </p>
                    </>,
                    <>
                        <h4>Update a referral</h4>
                        <p>
                            We can update the specialist's name on a referral issued by a Myhealth doctor within the
                            last 3 months.
                        </p>
                    </>,
                    <>
                        <h4>Allied health referrals</h4>
                        <p>
                            If you're requesting a referral under a Chronic Condition Management Plan, your plan must
                            already be current with Myhealth. If you don't have a current plan, a private referral may
                            be provided instead.
                        </p>
                    </>,
                ],
                notSuitable: [
                    <>
                        <h4>You need a new care plan</h4>
                        <p>
                            This service can't be used to create a new Mental Health Treatment Plan, Eating Disorder
                            Plan or Chronic Condition Management Plan.
                        </p>
                    </>,
                    <>
                        <h4>You have a new health concern</h4>
                        <p>
                            If you need a referral for a new medical condition or would like to discuss new symptoms,
                            please book a standard appointment.
                        </p>
                    </>,
                ],
            };
        case ExpressTelehealthPurpose.Diagnostics:
            return {
                criteria: [
                    <>
                        <h4>Routine tests only</h4>
                        <p>
                            This service is for routine pathology or imaging requests to monitor an existing health
                            condition or as part of your regular health care.
                        </p>
                    </>,
                    <>
                        <h4>Recent attendance</h4>
                        <p>You must have seen a Myhealth doctor in person within the last 12 months.</p>
                    </>,
                ],
                notSuitable: [
                    <>
                        <h4>You have serious or worsening symptoms</h4>
                        <p>
                            If you're experiencing severe symptoms or your existing health condition has suddenly become
                            worse, please book a standard appointment or seek urgent medical care if needed.
                        </p>
                    </>,
                    <>
                        <h4>You've had a serious injury</h4>
                        <p>
                            Recent injuries, significant trauma or suspected broken bones require an in-person
                            assessment.
                        </p>
                    </>,
                ],
            };
        case ExpressTelehealthPurpose.Unknown:
            return { criteria: [], notSuitable: [] };
    }
}

export function ExpressTelehealthPreScreening(props: Props) {
    return (
        <ScrollablePanel onClose={props.onClose} isOpen={props.isOpen}>
            <InnerExpressTelehealthPreScreening {...props} />
        </ScrollablePanel>
    );
}

function InnerExpressTelehealthPreScreening({ onClose, appointmentType, onAgree }: Props) {
    const expressReason = appointmentType
        ? expressTelehealthPurpose(appointmentType)
        : ExpressTelehealthPurpose.Unknown;
    const eligibilityContent = useMemo(() => getEligibility(expressReason), [expressReason]);

    return (
        <>
            <ScrollablePanelHeader>
                <h2 className="mb-2 text-lg font-semibold text-gray-900 sm:text-3xl">
                    {appointmentType?.appointmentName}
                </h2>
                <p>
                    In the interest of clinical safety, please confirm you meet the following eligibility criteria
                    before proceeding.
                </p>
            </ScrollablePanelHeader>
            <ScrollablePanelBody className="bg-gray-25 p-6">
                <div className="space-y-2">
                    <h3 className="text-base font-bold text-gray-900">Eligibility Criteria</h3>
                    {eligibilityContent.criteria.map((content, index) => (
                        <Card key={index}>
                            <div className="flex flex-row items-center gap-4">
                                <FeaturedIcon size="sm" icon={Check} color="success" className="flex-none" />
                                <div className="prose prose-sm leading-tight *:my-0">{content}</div>
                            </div>
                        </Card>
                    ))}
                </div>
                <div className="mt-4 space-y-2">
                    <h3 className="text-base font-bold text-gray-900">Not suitable for</h3>
                    {eligibilityContent.notSuitable.map((content, index) => (
                        <Card key={index}>
                            <div className="flex flex-row items-center gap-4">
                                <FeaturedIcon size="sm" color="error" className="flex-none" />
                                <div className="prose prose-sm leading-tight *:my-0">{content}</div>
                            </div>
                        </Card>
                    ))}
                </div>
            </ScrollablePanelBody>
            <ScrollablePanelFooter>
                {({ didScrollBottom }) => (
                    <>
                        <div className="prose prose-sm">{ClinicalDiscretion(expressReason)}</div>

                        <hr className="my-4" />
                        <div className="flex flex-row justify-end gap-3">
                            <Button type="button" color="gray" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                disabled={!didScrollBottom}
                                type="submit"
                                color="primary"
                                onClick={() => onAgree(expressReason)}
                            >
                                Agree and continue
                            </Button>
                        </div>
                    </>
                )}
            </ScrollablePanelFooter>
        </>
    );
}
